import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowDownTrayIcon, ArrowRightOnRectangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, Field, FormActions, formatMoney, formatNumber, getApiErrorMessage, inputClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { useAuthStore } from '../../store/auth.store';
import { cn } from '../../lib/utils';

type Kind = 'customer' | 'vendor';

interface Page<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface Me {
  kind: Kind;
  party: { code: string; name: string; email: string | null; phone: string | null; gstNumber: string | null; city: string | null };
  company: { companyName: string; email: string | null; phone: string | null } | null;
}

const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const tdClass = 'px-4 py-3 text-sm text-gray-700';

const day = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : String(value)).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const words = (value: string) => value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const orderStatus = (status: string) => (status === 'quote' ? 'Quotation' : status === 'partially_shipped' ? 'Part dispatched' : status === 'partially_received' ? 'Part received' : words(status));
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const download = async (url: string, fallback: string) => {
  try {
    const response = await api.get(url, { responseType: 'blob' });
    const named = /filename="?([^";]+)"?/.exec(String(response.headers['content-disposition'] ?? ''));
    const link = document.createElement('a');
    link.href = URL.createObjectURL(response.data as Blob);
    link.download = named?.[1] ?? fallback;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    let message = 'The PDF could not be made';
    if (data instanceof Blob) {
      try {
        message = JSON.parse(await data.text()).message || message;
      } catch {
        // keep the general message
      }
    }
    toast.error(message, { duration: 8000 });
  }
};

// A list of the portal with search, open/closed and pages
const usePortalList = <T,>(path: string) => {
  const [page, setPage] = useState(1);
  const [show, setShow] = useState<'' | 'open' | 'closed'>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const query = useQuery<Page<T>>({
    queryKey: ['portal', path, page, show, search],
    queryFn: async () => (await api.get(path, { params: { page, limit: 20, ...(show ? { show } : {}), ...(search ? { search } : {}) } })).data,
    placeholderData: keepPreviousData,
  });
  return { ...query, page, setPage, show, setShow: (value: '' | 'open' | 'closed') => { setShow(value); setPage(1); }, searchInput, setSearchInput };
};

const ListFrame: React.FC<{
  list: ReturnType<typeof usePortalList<any>>;
  placeholder: string;
  showLabels?: [string, string];
  empty: string;
  children: React.ReactNode;
}> = ({ list, placeholder, showLabels, empty, children }) => (
  <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
    <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row">
      <div className="relative flex-1">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input type="search" value={list.searchInput} onChange={(event) => list.setSearchInput(event.target.value)} placeholder={placeholder} aria-label={placeholder} className={cn(inputClass, 'pl-9')} />
      </div>
      {showLabels && (
        <select value={list.show} onChange={(event) => list.setShow(event.target.value as '' | 'open' | 'closed')} aria-label="Show" className={cn(inputClass, 'sm:w-48')}>
          <option value="">All</option>
          <option value="open">{showLabels[0]}</option>
          <option value="closed">{showLabels[1]}</option>
        </select>
      )}
    </div>
    {list.isLoading ? (
      <p className="px-4 py-10 text-center text-sm text-gray-500">Loading…</p>
    ) : list.error ? (
      <p className="px-4 py-10 text-center text-sm text-red-600">{getApiErrorMessage(list.error, 'This could not be loaded')}</p>
    ) : (list.data?.data ?? []).length === 0 ? (
      <p className="px-4 py-10 text-center text-sm text-gray-500">{empty}</p>
    ) : (
      <>
        <div className="overflow-x-auto">{children}</div>
        {list.data && list.data.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
            <span>Page {list.data.page} of {list.data.totalPages} · {formatNumber(list.data.total)} in all</span>
            <span className="flex gap-2">
              <button type="button" disabled={list.page <= 1} onClick={() => list.setPage(list.page - 1)} className={secondaryButtonClass}>Previous</button>
              <button type="button" disabled={list.page >= list.data.totalPages} onClick={() => list.setPage(list.page + 1)} className={secondaryButtonClass}>Next</button>
            </span>
          </div>
        )}
      </>
    )}
  </div>
);

// ---------------------------------------------------------------- customers

const Overview: React.FC = () => {
  const { data, isLoading } = useQuery<{ invoiced: number; paid: number; outstanding: number; overdue: number; payments: { id: string; paymentNumber: string; paymentDate: string; amount: number; mode: string | null; referenceNumber: string | null }[] }>({
    queryKey: ['portal', 'statement'],
    queryFn: async () => (await api.get('/portal/statement')).data,
  });
  if (isLoading || !data) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  const cards = [
    { label: 'Invoiced', value: data.invoiced },
    { label: 'Paid', value: data.paid },
    { label: 'To pay', value: data.outstanding },
    { label: 'Past due', value: data.overdue, alert: data.overdue > 0 },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="portal-statement">
        {cards.map((card) => (
          <div key={card.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{card.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold tabular-nums', card.alert ? 'text-red-600' : 'text-gray-900')}>{formatMoney(card.value)}</p>
          </div>
        ))}
      </div>
      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <h3 className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-900">Payments received</h3>
        {data.payments.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500">No payments yet.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr><th className={thClass}>Receipt</th><th className={thClass}>Date</th><th className={thClass}>How</th><th className={cn(thClass, 'text-right')}>Amount</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {data.payments.map((payment) => (
                <tr key={payment.id}>
                  <td className={tdClass}>{payment.paymentNumber}</td>
                  <td className={tdClass}>{day(payment.paymentDate)}</td>
                  <td className={tdClass}>{payment.mode ? words(payment.mode) : '—'}{payment.referenceNumber ? ` · ${payment.referenceNumber}` : ''}</td>
                  <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(payment.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const OrderPanel: React.FC<{ id: string }> = ({ id }) => {
  const { data: order, isLoading } = useQuery<any>({ queryKey: ['portal', 'order', id], queryFn: async () => (await api.get(`/portal/orders/${id}`)).data });
  if (isLoading || !order) return <p className="py-8 text-center text-sm text-gray-500">Loading…</p>;
  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
        <div><dt className="text-xs text-gray-500">Status</dt><dd className="font-medium text-gray-900">{orderStatus(order.status)}</dd></div>
        <div><dt className="text-xs text-gray-500">Ordered on</dt><dd>{day(order.orderDate)}</dd></div>
        <div><dt className="text-xs text-gray-500">{order.status === 'quote' ? 'Valid until' : 'Delivery by'}</dt><dd>{day(order.status === 'quote' ? order.quoteValidUntil : order.promisedDate ?? order.expectedDeliveryDate)}</dd></div>
        <div><dt className="text-xs text-gray-500">Total</dt><dd className="font-medium">{formatMoney(Number(order.grandTotal))}</dd></div>
      </dl>
      <table className="min-w-full divide-y divide-gray-200 rounded-md ring-1 ring-gray-200" data-testid="portal-order-lines">
        <thead className="bg-gray-50"><tr><th className={thClass}>Item</th><th className={cn(thClass, 'text-right')}>Ordered</th><th className={cn(thClass, 'text-right')}>Dispatched</th><th className={cn(thClass, 'text-right')}>Rate</th></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {order.items.map((item: any) => (
            <tr key={item.id}>
              <td className={tdClass}>{item.productName}{item.sku ? <span className="block text-xs text-gray-500">{item.sku}</span> : null}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatNumber(Number(item.quantity))} {item.unit ?? ''}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatNumber(Number(item.dispatchedQuantity))}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(Number(item.unitPrice))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {order.dispatches.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Dispatched</h4>
          <ul className="mt-2 space-y-1 text-sm text-gray-700">
            {order.dispatches.map((dispatch: any) => (
              <li key={dispatch.id}>{dispatch.challanNumber} on {day(dispatch.dispatchDate)}{dispatch.transporter ? ` · ${dispatch.transporter}` : ''}{dispatch.vehicleNumber ? ` · ${dispatch.vehicleNumber}` : ''}{dispatch.lrNumber ? ` · LR ${dispatch.lrNumber}` : ''}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Orders: React.FC = () => {
  const list = usePortalList<any>('/portal/orders');
  const [viewing, setViewing] = useState<any>(null);
  return (
    <>
      <ListFrame list={list} placeholder="Find an order number" showLabels={['Still to be delivered', 'Delivered or closed']} empty="No orders yet.">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr><th className={thClass}>Order</th><th className={thClass}>Date</th><th className={thClass}>Status</th><th className={thClass}>Delivery by</th><th className={cn(thClass, 'text-right')}>Dispatched</th><th className={cn(thClass, 'text-right')}>Total</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {(list.data?.data ?? []).map((order: any) => (
              <tr key={order.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setViewing(order)} data-testid="portal-row">
                <td className={cn(tdClass, 'font-medium text-indigo-700')}>{order.orderNumber}</td>
                <td className={tdClass}>{day(order.orderDate)}</td>
                <td className={tdClass}><span className={cn(badgeClass, 'bg-gray-100 text-gray-700')}>{orderStatus(order.status)}</span></td>
                <td className={tdClass}>{day(order.promisedDate ?? order.expectedDeliveryDate)}</td>
                <td className={cn(tdClass, 'text-right tabular-nums')}>{formatNumber(Number(order.dispatched))} of {formatNumber(Number(order.ordered))}</td>
                <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(Number(order.grandTotal))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListFrame>
      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title={viewing?.orderNumber ?? 'Order'} size="xl">
        {viewing && <OrderPanel id={viewing.id} />}
      </Modal>
    </>
  );
};

const Dispatches: React.FC = () => {
  const list = usePortalList<any>('/portal/dispatches');
  return (
    <ListFrame list={list} placeholder="Find a challan or order number" empty="Nothing dispatched yet.">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50"><tr><th className={thClass}>Challan</th><th className={thClass}>Date</th><th className={thClass}>Order</th><th className={thClass}>Transport</th><th className={cn(thClass, 'text-right')}>Quantity</th><th className={thClass}><span className="sr-only">PDF</span></th></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {(list.data?.data ?? []).map((dispatch: any) => (
            <tr key={dispatch.id} data-testid="portal-row">
              <td className={cn(tdClass, 'font-medium text-gray-900')}>{dispatch.challanNumber}</td>
              <td className={tdClass}>{day(dispatch.dispatchDate)}</td>
              <td className={tdClass}>{dispatch.salesOrderNumber}</td>
              <td className={tdClass}>{[dispatch.transporter, dispatch.vehicleNumber, dispatch.lrNumber ? `LR ${dispatch.lrNumber}` : null].filter(Boolean).join(' · ') || '—'}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatNumber(Number(dispatch.quantity))}</td>
              <td className={cn(tdClass, 'text-right')}>
                <button type="button" onClick={() => download(`/portal/dispatches/${dispatch.id}/pdf`, `${dispatch.challanNumber}.pdf`)} aria-label={`Download challan ${dispatch.challanNumber}`} className="text-indigo-600 hover:text-indigo-800"><ArrowDownTrayIcon className="h-5 w-5" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListFrame>
  );
};

const Invoices: React.FC = () => {
  const list = usePortalList<any>('/portal/invoices');
  return (
    <ListFrame list={list} placeholder="Find an invoice number" showLabels={['Still to pay', 'Paid']} empty="No invoices yet.">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50"><tr><th className={thClass}>Invoice</th><th className={thClass}>Date</th><th className={thClass}>Due</th><th className={cn(thClass, 'text-right')}>Total</th><th className={cn(thClass, 'text-right')}>Paid</th><th className={cn(thClass, 'text-right')}>To pay</th><th className={thClass}><span className="sr-only">PDF</span></th></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {(list.data?.data ?? []).map((invoice: any) => (
            <tr key={invoice.id} data-testid="portal-row">
              <td className={cn(tdClass, 'font-medium text-gray-900')}>{invoice.invoiceNumber}</td>
              <td className={tdClass}>{day(invoice.invoiceDate)}</td>
              <td className={cn(tdClass, invoice.overdue && 'font-medium text-red-600')}>{day(invoice.dueDate)}{invoice.overdue ? ' · past due' : ''}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(invoice.grandTotal)}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(invoice.paidAmount)}</td>
              <td className={cn(tdClass, 'text-right font-medium tabular-nums')}>{formatMoney(invoice.outstanding)}</td>
              <td className={cn(tdClass, 'text-right')}>
                <button type="button" onClick={() => download(`/portal/invoices/${invoice.id}/pdf`, `${invoice.invoiceNumber}.pdf`)} aria-label={`Download invoice ${invoice.invoiceNumber}`} className="text-indigo-600 hover:text-indigo-800"><ArrowDownTrayIcon className="h-5 w-5" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListFrame>
  );
};

// ---------------------------------------------------------------- vendors

const PurchaseOrderPanel: React.FC<{ id: string }> = ({ id }) => {
  const queryClient = useQueryClient();
  const { data: order, isLoading } = useQuery<any>({ queryKey: ['portal', 'purchase-order', id], queryFn: async () => (await api.get(`/portal/purchase-orders/${id}`)).data });
  const [promisedDate, setPromisedDate] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (order) {
      setPromisedDate(order.vendorPromisedDate ?? String(order.expectedDate ?? '').slice(0, 10));
      setNote(order.vendorNote ?? '');
    }
  }, [order]);
  const acknowledge = useMutation({
    mutationFn: async () => (await api.post(`/portal/purchase-orders/${id}/acknowledge`, { promisedDate: promisedDate || null, note: note.trim() || null })).data,
    onSuccess: (updated) => {
      toast.success('Thank you; the buyer can see your confirmation');
      queryClient.setQueryData(['portal', 'purchase-order', id], updated);
      queryClient.invalidateQueries({ queryKey: ['portal', '/portal/purchase-orders'] });
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Could not confirm the order'), { duration: 8000 }),
  });
  if (isLoading || !order) return <p className="py-8 text-center text-sm text-gray-500">Loading…</p>;
  const open = ['approved', 'partially_received'].includes(order.status);
  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
        <div><dt className="text-xs text-gray-500">Status</dt><dd className="font-medium text-gray-900">{orderStatus(order.status)}</dd></div>
        <div><dt className="text-xs text-gray-500">Ordered on</dt><dd>{day(order.orderDate)}</dd></div>
        <div><dt className="text-xs text-gray-500">Wanted by</dt><dd>{day(order.expectedDate)}</dd></div>
        <div><dt className="text-xs text-gray-500">Total</dt><dd className="font-medium">{formatMoney(Number(order.grandTotal))}</dd></div>
      </dl>
      <table className="min-w-full divide-y divide-gray-200 rounded-md ring-1 ring-gray-200">
        <thead className="bg-gray-50"><tr><th className={thClass}>Item</th><th className={cn(thClass, 'text-right')}>Ordered</th><th className={cn(thClass, 'text-right')}>Received</th><th className={cn(thClass, 'text-right')}>Rate</th></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {order.items.map((item: any) => (
            <tr key={item.id}>
              <td className={tdClass}>{item.productName}{item.sku ? <span className="block text-xs text-gray-500">{item.sku}</span> : null}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatNumber(Number(item.quantity))} {item.unit ?? ''}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatNumber(Number(item.receivedQuantity))}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(Number(item.unitPrice))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {order.receipts.length > 0 && (
        <ul className="space-y-1 text-sm text-gray-700">
          {order.receipts.map((receipt: any) => (
            <li key={receipt.id}>Received {day(receipt.receiptDate)} ({receipt.grnNumber}): {formatNumber(Number(receipt.accepted))} accepted{Number(receipt.rejected) > 0 ? `, ${formatNumber(Number(receipt.rejected))} rejected` : ''}</li>
          ))}
        </ul>
      )}
      {open ? (
        <form
          className="space-y-3 rounded-md bg-indigo-50 p-4"
          onSubmit={(event) => {
            event.preventDefault();
            acknowledge.mutate();
          }}
        >
          <p className="text-sm font-medium text-indigo-900">{order.vendorAcknowledgedAt ? `You confirmed this order on ${day(order.vendorAcknowledgedAt)}. You can change your date or note.` : 'Confirm that you have this order, and when you will deliver.'}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="We will deliver by" htmlFor="ack-date">
              <input id="ack-date" type="date" min={today()} value={promisedDate} onChange={(event) => setPromisedDate(event.target.value)} className={inputClass} />
            </Field>
            <Field label="Note to the buyer" htmlFor="ack-note" className="sm:col-span-2">
              <input id="ack-note" type="text" maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} className={inputClass} />
            </Field>
          </div>
          <FormActions>
            <button type="submit" disabled={acknowledge.isPending} className={primaryButtonClass}>{acknowledge.isPending ? 'Sending…' : order.vendorAcknowledgedAt ? 'Update Confirmation' : 'Confirm Order'}</button>
          </FormActions>
        </form>
      ) : order.vendorAcknowledgedAt ? (
        <p className="text-sm text-gray-600">Confirmed on {day(order.vendorAcknowledgedAt)}{order.vendorPromisedDate ? `, to deliver by ${day(order.vendorPromisedDate)}` : ''}.</p>
      ) : null}
    </div>
  );
};

const PurchaseOrders: React.FC = () => {
  const list = usePortalList<any>('/portal/purchase-orders');
  const [viewing, setViewing] = useState<any>(null);
  return (
    <>
      <ListFrame list={list} placeholder="Find a purchase order number" showLabels={['Still to deliver', 'Delivered or cancelled']} empty="No purchase orders yet.">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr><th className={thClass}>Order</th><th className={thClass}>Date</th><th className={thClass}>Status</th><th className={thClass}>Wanted by</th><th className={thClass}>Your confirmation</th><th className={cn(thClass, 'text-right')}>Received</th><th className={cn(thClass, 'text-right')}>Total</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {(list.data?.data ?? []).map((order: any) => (
              <tr key={order.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setViewing(order)} data-testid="portal-row">
                <td className={cn(tdClass, 'font-medium text-indigo-700')}>{order.orderNumber}</td>
                <td className={tdClass}>{day(order.orderDate)}</td>
                <td className={tdClass}><span className={cn(badgeClass, 'bg-gray-100 text-gray-700')}>{orderStatus(order.status)}</span></td>
                <td className={tdClass}>{day(order.expectedDate)}</td>
                <td className={tdClass}>{order.vendorAcknowledgedAt ? `Confirmed${order.vendorPromisedDate ? `, by ${day(order.vendorPromisedDate)}` : ''}` : <span className="text-amber-700">Not yet</span>}</td>
                <td className={cn(tdClass, 'text-right tabular-nums')}>{formatNumber(Number(order.received))} of {formatNumber(Number(order.ordered))}</td>
                <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(Number(order.grandTotal))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListFrame>
      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title={viewing?.orderNumber ?? 'Purchase order'} size="xl">
        {viewing && <PurchaseOrderPanel id={viewing.id} />}
      </Modal>
    </>
  );
};

const Bills: React.FC = () => {
  const list = usePortalList<any>('/portal/bills');
  return (
    <ListFrame list={list} placeholder="Find your invoice number" showLabels={['Still to be paid', 'Paid']} empty="No invoices of yours are booked yet.">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50"><tr><th className={thClass}>Your invoice</th><th className={thClass}>Date</th><th className={thClass}>Due</th><th className={thClass}>Purchase order</th><th className={cn(thClass, 'text-right')}>Total</th><th className={cn(thClass, 'text-right')}>Paid</th><th className={cn(thClass, 'text-right')}>To be paid</th></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {(list.data?.data ?? []).map((bill: any) => (
            <tr key={bill.id} data-testid="portal-row">
              <td className={cn(tdClass, 'font-medium text-gray-900')}>{bill.vendorInvoiceNumber}<span className="block text-xs text-gray-500">Booked as {bill.billNumber}</span></td>
              <td className={tdClass}>{day(bill.billDate)}</td>
              <td className={tdClass}>{day(bill.dueDate)}</td>
              <td className={tdClass}>{bill.purchaseOrderNumber ?? '—'}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(bill.grandTotal)}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(bill.paidAmount)}</td>
              <td className={cn(tdClass, 'text-right font-medium tabular-nums')}>{formatMoney(bill.outstanding)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListFrame>
  );
};

const Payments: React.FC = () => {
  const list = usePortalList<any>('/portal/payments');
  return (
    <ListFrame list={list} placeholder="Find a payment number" empty="No payments yet.">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50"><tr><th className={thClass}>Payment</th><th className={thClass}>Date</th><th className={thClass}>How</th><th className={thClass}>For your invoices</th><th className={cn(thClass, 'text-right')}>Amount</th></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {(list.data?.data ?? []).map((payment: any) => (
            <tr key={payment.id} data-testid="portal-row">
              <td className={cn(tdClass, 'font-medium text-gray-900')}>{payment.paymentNumber}</td>
              <td className={tdClass}>{day(payment.paymentDate)}</td>
              <td className={tdClass}>{payment.mode ? words(payment.mode) : '—'}{payment.referenceNumber ? ` · ${payment.referenceNumber}` : ''}</td>
              <td className={tdClass}>{(payment.bills ?? []).map((bill: any) => `${bill.vendorInvoiceNumber} (${formatMoney(Number(bill.amount))})`).join(', ') || '—'}</td>
              <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(payment.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListFrame>
  );
};

// ---------------------------------------------------------------- the portal

const Portal: React.FC = () => {
  const { user, logout } = useAuthStore();
  const kind = (user as { portal?: { kind: Kind } } | null)?.portal?.kind ?? 'customer';
  const tabs = kind === 'customer'
    ? [{ key: 'overview', label: 'Overview' }, { key: 'orders', label: 'Orders' }, { key: 'dispatches', label: 'Dispatches' }, { key: 'invoices', label: 'Invoices' }]
    : [{ key: 'purchase-orders', label: 'Purchase Orders' }, { key: 'bills', label: 'Your Invoices' }, { key: 'payments', label: 'Payments' }];
  const [tab, setTab] = useState(tabs[0].key);
  const { data: me } = useQuery<Me>({ queryKey: ['portal', 'me'], queryFn: async () => (await api.get('/portal/me')).data });

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-indigo-600">{me?.company?.companyName ?? 'RightFitERP'} · {kind === 'customer' ? 'Customer' : 'Vendor'} portal</p>
            <h1 className="text-xl font-semibold text-gray-900" data-testid="portal-party">{me?.party.name ?? ''}</h1>
          </div>
          <button type="button" onClick={() => { logout(); window.location.href = '/login'; }} className={secondaryButtonClass}>
            <ArrowRightOnRectangleIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Sign Out
          </button>
        </div>
        <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4" role="tablist">
          {tabs.map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => setTab(item.key)} className={cn('-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {item.label}
            </button>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6">
        {tab === 'overview' && <Overview />}
        {tab === 'orders' && <Orders />}
        {tab === 'dispatches' && <Dispatches />}
        {tab === 'invoices' && <Invoices />}
        {tab === 'purchase-orders' && <PurchaseOrders />}
        {tab === 'bills' && <Bills />}
        {tab === 'payments' && <Payments />}
        {me?.company && (me.company.email || me.company.phone) && (
          <p className="text-center text-xs text-gray-500">Questions? Write to {me.company.email ?? ''}{me.company.email && me.company.phone ? ' or call ' : ''}{me.company.phone ?? ''}.</p>
        )}
      </main>
    </div>
  );
};

export default Portal;
